import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Search, RefreshCw, Edit3, Tag, X, Save, AlertCircle, Trash2 } from "lucide-react";

// Build the backend image-proxy URL for a Discord CDN URL.
const proxyImage = (url) => {
    if (!url) return "";
    try {
        const base = api.defaults?.baseURL || "";
        return `${base}/bridge/image?u=${encodeURIComponent(url)}`;
    } catch {
        return url;
    }
};

export default function CatalogoDiscordAdmin() {
    const [products, setProducts] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [imageMap, setImageMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [cached, setCached] = useState(false);
    const [q, setQ] = useState("");
    const [filterCat, setFilterCat] = useState("");
    const [onlyEmpty, setOnlyEmpty] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async (force = false) => {
        setLoading(true);
        setError("");
        try {
            const [invRes, catRes] = await Promise.all([
                api.get(`/bridge/inventario${force ? "?force=true" : ""}`),
                api.get("/bridge/categorias"),
            ]);
            const items = invRes.data?.products || [];
            setProducts(items);
            setCategorias(catRes.data?.categorias || []);
            setCached(!!invRes.data?.cached);
            setLoading(false);

            // Batch-refresh image URLs so admin thumbnails load without flooding the bot.
            const urls = items.map((p) => p.foto).filter((u) => typeof u === "string" && u);
            if (urls.length > 0) {
                const chunks = [];
                for (let i = 0; i < urls.length; i += 60) chunks.push(urls.slice(i, i + 60));
                const results = await Promise.all(
                    chunks.map((c) =>
                        api.post("/bridge/images/batch", { urls: c })
                            .then((r) => r.data?.mapping || {})
                            .catch(() => ({}))
                    )
                );
                setImageMap(Object.assign({}, ...results));
            }
        } catch (e) {
            setError(formatApiErrorDetail(e, "No se pudo conectar con el bot."));
            setLoading(false);
        }
    };

    useEffect(() => { load(false); }, []);

    const resolveImage = (url) => {
        if (!url) return "";
        return imageMap[url] || proxyImage(url);
    };

    const filtered = useMemo(() => {
        const needle = (q || "").trim().toLowerCase();
        return products.filter((p) => {
            if (filterCat && (p.categoria || "") !== filterCat) return false;
            if (onlyEmpty && (p.categoria || "").trim()) return false;
            if (!needle) return true;
            const hay = `${p.id || ""} ${p.nombre || ""} ${p.descripcion || ""}`.toLowerCase();
            return hay.includes(needle);
        });
    }, [products, q, filterCat, onlyEmpty]);

    const emptyCount = products.filter((p) => !(p.categoria || "").trim()).length;

    const invalidateAndRefresh = async () => {
        try {
            await api.post("/admin/bridge/invalidate-cache");
            await load(true);
            toast.success("Caché limpiada y recargada");
        } catch (e) {
            toast.error(formatApiErrorDetail(e, "Error limpiando caché"));
        }
    };

    const [legacyCount, setLegacyCount] = useState(null);
    const [purging, setPurging] = useState(false);
    const [showDanger, setShowDanger] = useState(false);
    useEffect(() => {
        api.get("/admin/products/count").then((r) => setLegacyCount(r.data?.count ?? 0)).catch(() => setLegacyCount(0));
    }, []);

    const purgeLegacy = async () => {
        const phrase = window.prompt(
            `Vas a eliminar ${legacyCount} productos legacy de la base de datos web (y sus imágenes).\n` +
            `El inventario de Discord NO se toca.\n\n` +
            `Escribe PURGAR para confirmar:`,
        );
        if (phrase !== "PURGAR") {
            toast.info("Operación cancelada");
            return;
        }
        setPurging(true);
        try {
            const { data } = await api.post("/admin/products/purge-all", { confirm: "PURGAR" });
            toast.success(
                `Eliminados ${data.products_deleted} productos y ${data.images_deleted}/${data.images_total || 0} imágenes`,
                { duration: 8000 },
            );
            setLegacyCount(0);
        } catch (e) {
            toast.error(formatApiErrorDetail(e, "No se pudo purgar"));
        } finally {
            setPurging(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="catalogo-discord-section">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h3 className="font-display text-xl">Catálogo Discord</h3>
                    <p className="text-xs text-[var(--tb-text-2)] mt-1">
                        Lee en vivo del bot y permite asignar o cambiar la <strong>categoría</strong>, el nombre, la descripción o el precio. Los cambios se guardan en Discord y se reflejan en la tienda.
                        {cached && <span className="ml-2 text-[var(--tb-muted)]">· (caché)</span>}
                    </p>
                </div>
                <button
                    onClick={invalidateAndRefresh}
                    className="tb-btn-secondary !text-xs"
                    data-testid="catalogo-discord-refresh"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Refrescar desde bot
                </button>
            </div>

            {error && (
                <div className="tb-card p-4 flex items-center gap-3 border-red-500/40" data-testid="catalogo-discord-error">
                    <AlertCircle className="w-4 h-4 text-red-400" />
                    <span className="text-sm">{error}</span>
                </div>
            )}

            <div className="grid sm:grid-cols-4 gap-3">
                <div className="relative sm:col-span-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tb-muted)]" />
                    <input
                        className="tb-input pl-9"
                        placeholder="Buscar por ID, nombre o descripción"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="catalogo-discord-search"
                    />
                </div>
                <select
                    className="tb-input"
                    value={filterCat}
                    onChange={(e) => setFilterCat(e.target.value)}
                    data-testid="catalogo-discord-filter-cat"
                >
                    <option value="">Todas las categorías</option>
                    {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm text-[var(--tb-text-2)]">
                    <input
                        type="checkbox"
                        checked={onlyEmpty}
                        onChange={(e) => setOnlyEmpty(e.target.checked)}
                        data-testid="catalogo-discord-only-empty"
                    />
                    Sin categoría ({emptyCount})
                </label>
            </div>

            <div className="text-xs text-[var(--tb-text-2)]">
                {loading ? "Cargando..." : `${filtered.length} de ${products.length} productos`}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map((p) => (
                    <div
                        key={p.id}
                        className="tb-card p-3 flex gap-3"
                        data-testid={`catalogo-discord-row-${p.id}`}
                    >
                        <div className="w-16 h-20 flex-shrink-0 bg-[var(--tb-surface-2)] overflow-hidden rounded">
                            {p.foto ? (
                                <img src={resolveImage(p.foto)} alt={p.nombre} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-[10px] text-[var(--tb-muted)]">Sin foto</div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] font-mono text-[var(--tb-muted)]">{p.id}</span>
                                <span className="text-[10px] text-[var(--tb-text-2)]">· {p.cantidad_disponible} disp</span>
                            </div>
                            <div className="text-sm font-medium truncate" title={p.nombre}>{p.nombre}</div>
                            <div className="flex items-center gap-1 mt-1">
                                <Tag className="w-3 h-3 text-[var(--tb-gold)]" />
                                {p.categoria ? (
                                    <span className="text-[11px] text-[var(--tb-text)]">{p.categoria}</span>
                                ) : (
                                    <span className="text-[11px] italic text-[var(--tb-warm)]">Sin categoría</span>
                                )}
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <span className="text-sm text-[var(--tb-gold)]">${Number(p.precio_venta || 0).toFixed(2)}</span>
                                <button
                                    onClick={() => setEditing(p)}
                                    className="tb-btn-secondary !text-[11px] !px-2 !py-1"
                                    data-testid={`catalogo-discord-edit-${p.id}`}
                                >
                                    <Edit3 className="w-3 h-3" /> Editar
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <EditProductModal
                    product={editing}
                    categorias={categorias}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await load(true); }}
                />
            )}

            {/* ------------------------------ DANGER ZONE ------------------------------ */}
            <div className="mt-10 border-t border-[var(--tb-border)] pt-6">
                <button
                    onClick={() => setShowDanger((v) => !v)}
                    className="text-xs text-[var(--tb-muted)] hover:text-red-400 transition inline-flex items-center gap-1"
                    data-testid="catalogo-discord-danger-toggle"
                >
                    <AlertCircle className="w-3.5 h-3.5" /> {showDanger ? "Ocultar" : "Mostrar"} opciones avanzadas
                </button>
                {showDanger && (
                    <div className="mt-4 tb-card p-4 border-red-500/40 space-y-3" data-testid="catalogo-discord-danger-panel">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-[var(--tb-text-2)] space-y-1">
                                <p className="text-sm font-medium text-[var(--tb-text)]">Purgar catálogo legacy</p>
                                <p>
                                    Elimina <strong>{legacyCount ?? "…"}</strong> productos antiguos de la base de datos de la web y sus imágenes en Object Storage (libera memoria).
                                    El inventario de <strong>Discord NO se toca</strong>: la tienda sigue leyendo desde el bot.
                                </p>
                                <p className="text-[var(--tb-muted)]">
                                    Las órdenes históricas conservan su snapshot y seguirán visibles. Esta acción es irreversible.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={purgeLegacy}
                            disabled={purging || !legacyCount}
                            className="tb-btn-primary !bg-red-500 hover:!bg-red-600 !text-white !text-xs disabled:opacity-50"
                            data-testid="catalogo-discord-purge"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                            {purging ? "Eliminando..." : (legacyCount ? `Eliminar ${legacyCount} productos` : "Ya está limpio")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function EditProductModal({ product, categorias, onClose, onSaved }) {
    const [nombre, setNombre] = useState(product.nombre || "");
    const [descripcion, setDescripcion] = useState(product.descripcion || "");
    const [categoria, setCategoria] = useState(product.categoria || "");
    const [newCat, setNewCat] = useState("");
    const [precioVenta, setPrecioVenta] = useState(String(product.precio_venta ?? ""));
    const [foto, setFoto] = useState(product.foto || "");
    const [saving, setSaving] = useState(false);

    const resolveCategoria = () => {
        const c = (newCat || "").trim();
        return c ? c : categoria;
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                id: product.id,
                nombre: nombre !== product.nombre ? nombre : undefined,
                descripcion: descripcion !== product.descripcion ? descripcion : undefined,
                categoria: resolveCategoria() !== product.categoria ? resolveCategoria() : undefined,
                precio_venta: Number(precioVenta) !== Number(product.precio_venta || 0) ? Number(precioVenta) : undefined,
                foto: foto !== product.foto ? foto : undefined,
            };
            // Strip undefined so we send ONLY the fields that changed.
            Object.keys(body).forEach((k) => { if (body[k] === undefined) delete body[k]; });
            if (Object.keys(body).length === 1) {
                toast.info("No hay cambios para guardar");
                setSaving(false);
                return;
            }
            await api.post("/bridge/producto/editar", body);
            toast.success("Guardado en Discord");
            await onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e, "No se pudo guardar"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={onClose}
            data-testid="catalogo-discord-modal"
        >
            <div
                className="tb-card max-w-lg w-full p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-[var(--tb-muted)] hover:text-[var(--tb-text)]"
                    aria-label="Cerrar"
                    data-testid="catalogo-discord-modal-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3 mb-5">
                    <span className="text-[10px] font-mono text-[var(--tb-muted)]">{product.id}</span>
                </div>
                <h3 className="font-display text-lg mb-4">Editar producto</h3>

                <div className="space-y-3">
                    <div>
                        <label className="tb-overline mb-1">Nombre</label>
                        <input className="tb-input" value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="catalogo-discord-nombre" />
                    </div>
                    <div>
                        <label className="tb-overline mb-1">Descripción</label>
                        <textarea
                            className="tb-input"
                            rows={3}
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            data-testid="catalogo-discord-desc"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="tb-overline mb-1">Categoría existente</label>
                            <select
                                className="tb-input"
                                value={categoria}
                                onChange={(e) => { setCategoria(e.target.value); setNewCat(""); }}
                                data-testid="catalogo-discord-cat-existing"
                            >
                                <option value="">(sin categoría)</option>
                                {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="tb-overline mb-1">...o crear nueva</label>
                            <input
                                className="tb-input"
                                placeholder="Vestidos, Blusas, etc."
                                value={newCat}
                                onChange={(e) => setNewCat(e.target.value)}
                                data-testid="catalogo-discord-cat-new"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="tb-overline mb-1">Precio venta (USD)</label>
                        <input
                            type="number"
                            step="0.01"
                            className="tb-input"
                            value={precioVenta}
                            onChange={(e) => setPrecioVenta(e.target.value)}
                            data-testid="catalogo-discord-precio"
                        />
                    </div>
                    <div>
                        <label className="tb-overline mb-1">URL de la foto</label>
                        <input
                            className="tb-input"
                            value={foto}
                            onChange={(e) => setFoto(e.target.value)}
                            data-testid="catalogo-discord-foto"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 mt-6">
                    <button onClick={onClose} className="tb-btn-secondary !text-sm" data-testid="catalogo-discord-modal-cancel">Cancelar</button>
                    <button
                        onClick={save}
                        disabled={saving}
                        className="tb-btn-primary !text-sm disabled:opacity-60"
                        data-testid="catalogo-discord-modal-save"
                    >
                        <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
                    </button>
                </div>
            </div>
        </div>
    );
}
